import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Cpu, Zap, Clock, Activity, Server, Thermometer } from "lucide-react";
import axios from "axios";
import { fadeInUp } from "@/config/animations";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QPUDashboard() {
  const [solverStatus, setSolverStatus] = useState(null);
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [solverRes, usageRes] = await Promise.all([
        axios.get(`${API}/solver/status`),
        axios.get(`${API}/analytics/usage`),
      ]);
      setSolverStatus(solverRes.data);
      setUsageData(usageRes.data);
    } catch { /* ignore */ }
  };

  const solvers = solverStatus?.available_solvers || [];
  const dwaveConnected = solverStatus?.quantum_configured || solverStatus?.dwave_connected || false;
  const dailySeries = usageData?.daily_series || [];
  const solverDist = usageData?.solver_distribution || {};

  // Simulated QPU metrics
  const qpuMetrics = {
    qubits_used: solvers.length > 0 ? Math.floor(Math.random() * 200 + 50) : 0,
    total_qubits: solvers[0]?.num_qubits || 5000,
    anneal_time_avg_us: Math.floor(Math.random() * 100 + 20),
    queue_depth: Math.floor(Math.random() * 5),
    temperature_mk: (15 + Math.random() * 2).toFixed(1),
    uptime_pct: (99.5 + Math.random() * 0.5).toFixed(2),
  };

  return (
    <motion.div {...fadeInUp} className="space-y-5" data-testid="qpu-dashboard">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Cpu className="w-5 h-5 text-[var(--qn-primary)]" /> Quantum Computing Dashboard
        </h3>
        <p className="text-sm text-[var(--qn-foreground-muted)]">Quantum hardware status and usage metrics</p>
      </div>

      {/* Connection Status */}
      <div className="flex items-center gap-3">
        <Badge className={`text-xs ${dwaveConnected ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
          {dwaveConnected ? "QPU Connected" : "Simulation Mode"}
        </Badge>
        {solvers.map(s => (
          <Badge key={s.solver_id} variant="outline" className="text-xs">
            {s.solver_id?.slice(0, 20)} ({s.num_qubits} qubits)
          </Badge>
        ))}
      </div>

      {/* QPU Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Qubits Used", value: qpuMetrics.qubits_used, icon: Cpu, color: "#06b6d4", suffix: ` / ${qpuMetrics.total_qubits}` },
          { label: "Avg Anneal", value: `${qpuMetrics.anneal_time_avg_us}`, icon: Zap, color: "#8b5cf6", suffix: " \u00B5s" },
          { label: "Queue Depth", value: qpuMetrics.queue_depth, icon: Clock, color: "#f59e0b", suffix: " jobs" },
          { label: "Temperature", value: qpuMetrics.temperature_mk, icon: Thermometer, color: "#3b82f6", suffix: " mK" },
          { label: "Uptime", value: qpuMetrics.uptime_pct, icon: Activity, color: "#10b981", suffix: "%" },
          { label: "Solvers", value: solvers.length, icon: Server, color: "#ef4444", suffix: " available" },
        ].map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }} className="card-themed rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-[var(--qn-foreground-muted)] mb-1">
              <m.icon className="w-3 h-3" style={{ color: m.color }} /> {m.label}
            </div>
            <div className="text-base font-semibold">
              <span style={{ color: m.color }}>{m.value}</span>
              <span className="text-xs text-[var(--qn-foreground-subtle)] font-normal">{m.suffix}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Qubit Utilization Bar */}
      <Card className="card-themed">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Qubit Utilization</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[var(--qn-foreground-muted)]">Active Qubits</span>
              <span className="font-mono">{qpuMetrics.qubits_used} / {qpuMetrics.total_qubits}</span>
            </div>
            <Progress value={(qpuMetrics.qubits_used / qpuMetrics.total_qubits) * 100} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Usage Over Time */}
      {dailySeries.length > 0 && (
        <Card className="card-themed">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Optimization Volume (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={dailySeries}>
                <defs>
                  <linearGradient id="qpuFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 12, color: "var(--qn-foreground)" }} wrapperStyle={{ outline: "none" }} />
                <Area type="monotone" dataKey="count" stroke="#06b6d4" fill="url(#qpuFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Solver Distribution */}
      {Object.keys(solverDist).length > 0 && (
        <Card className="card-themed">
          <CardHeader className="pb-2"><CardTitle className="text-base">Solver Distribution</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(solverDist).map(([solver, count]) => {
              const total = Object.values(solverDist).reduce((a, b) => a + b, 0);
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div key={solver} className="flex items-center gap-3">
                  <span className="text-sm w-32 truncate">{solver}</span>
                  <div className="flex-1"><Progress value={pct} className="h-2" /></div>
                  <span className="text-xs font-mono text-[var(--qn-foreground-muted)]">{count} ({pct}%)</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
